import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Mic, Square, Play, Pause, RotateCcw, Save, Trash2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { apiUpload } from "@/lib/api";

/**
 * Voice recorder — mobile-first.
 *
 * Uses MediaRecorder for audio capture (audio/webm;opus). Simultaneously runs
 * SpeechRecognition (when available) to fill a live transcript. Neither is
 * required for save; both are best-effort. On save, POSTs multipart to
 * /voice-notes with the audio blob + transcript + duration.
 *
 * Props:
 *   onSaved(note)  — called with the created voice-note payload after save
 *   onCancel()     — called when user cancels
 */
export default function VoiceRecorder({ onSaved, onCancel }) {
  const [state, setState] = useState("idle"); // idle | recording | stopped
  const [duration, setDuration] = useState(0);  // seconds
  const [transcript, setTranscript] = useState("");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [micError, setMicError] = useState(null);
  const [supportsSR, setSupportsSR] = useState(true);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const recognitionRef = useRef(null);
  const timerRef = useRef(null);
  const startedAtRef = useRef(null);
  const audioBlobRef = useRef(null);
  const audioUrlRef = useRef(null);
  const audioEl = useRef(null);
  const interimRef = useRef("");

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) setSupportsSR(false);
    return () => cleanup();
    // eslint-disable-next-line
  }, []);

  const cleanup = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try { recognitionRef.current?.stop?.(); } catch {}
    recognitionRef.current = null;
    try { mediaRecorderRef.current?.stop?.(); } catch {}
    mediaRecorderRef.current = null;
    try { streamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch {}
    streamRef.current = null;
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
  }, []);

  const resetAll = () => {
    cleanup();
    audioBlobRef.current = null;
    chunksRef.current = [];
    setState("idle");
    setDuration(0);
    setTranscript("");
    setTitle("");
    setPlaying(false);
    interimRef.current = "";
  };

  const start = async () => {
    if (state === "recording") return;
    setMicError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Pick the best supported mime
      let mime = "";
      if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) mime = "audio/webm;codecs=opus";
      else if (MediaRecorder.isTypeSupported("audio/webm")) mime = "audio/webm";
      else if (MediaRecorder.isTypeSupported("audio/mp4")) mime = "audio/mp4";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        audioBlobRef.current = blob;
        if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = URL.createObjectURL(blob);
        if (audioEl.current) audioEl.current.src = audioUrlRef.current;
        setState("stopped");
      };
      mr.start(1000); // gather chunks every second

      startedAtRef.current = Date.now();
      setDuration(0);
      timerRef.current = setInterval(() => {
        setDuration(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }, 250);

      // Speech recognition (best-effort)
      const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SR) {
        try {
          const recog = new SR();
          recog.continuous = true;
          recog.interimResults = true;
          recog.lang = navigator.language || "en-US";
          recog.onresult = (evt) => {
            let finalText = "";
            let interim = "";
            for (let i = evt.resultIndex; i < evt.results.length; i++) {
              const res = evt.results[i];
              if (res.isFinal) finalText += res[0].transcript;
              else interim += res[0].transcript;
            }
            if (finalText) {
              setTranscript((prev) => (prev ? prev + " " : "") + finalText.trim());
              interimRef.current = "";
            } else {
              interimRef.current = interim;
              // Force re-render for interim display
              setTranscript((prev) => prev);
            }
          };
          recog.onerror = (e) => {
            // Not fatal — audio still records. Some browsers throw a benign
            // 'no-speech' error on silence; ignore it.
            if (e.error && e.error !== "no-speech") {
              // eslint-disable-next-line no-console
              console.debug("[voice-recorder] speech error", e.error);
            }
          };
          recog.onend = () => { /* auto-stops when recording stops */ };
          recognitionRef.current = recog;
          recog.start();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.debug("[voice-recorder] SR init failed", e);
        }
      }
      setState("recording");
    } catch (err) {
      setMicError(err?.message || "Microphone access denied.");
      toast.error(err?.message || "Microphone access denied");
    }
  };

  const stop = () => {
    if (state !== "recording") return;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try { recognitionRef.current?.stop?.(); } catch {}
    try { mediaRecorderRef.current?.stop?.(); } catch {}
    try { streamRef.current?.getTracks?.().forEach((t) => t.stop()); } catch {}
  };

  const togglePlayback = () => {
    if (!audioEl.current) return;
    if (playing) { audioEl.current.pause(); setPlaying(false); }
    else { audioEl.current.play(); setPlaying(true); }
  };

  const save = async () => {
    if (!audioBlobRef.current) return;
    setSaving(true);
    try {
      const fd = new FormData();
      const ext = audioBlobRef.current.type.includes("mp4") ? ".m4a" : ".webm";
      const file = new File([audioBlobRef.current], `voice-${Date.now()}${ext}`, { type: audioBlobRef.current.type });
      fd.append("file", file);
      if (title.trim()) fd.append("title", title.trim());
      const finalTranscript = (transcript + (interimRef.current ? " " + interimRef.current : "")).trim();
      if (finalTranscript) fd.append("transcript", finalTranscript);
      fd.append("duration_seconds", String(duration));
      const note = await apiUpload("/voice-notes", fd);
      toast.success("Voice note saved");
      onSaved?.(note);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Formatted mm:ss
  const fmt = (s) => {
    const m = Math.floor(s / 60), r = s % 60;
    return `${m}:${String(r).padStart(2, "0")}`;
  };

  const displayTranscript = transcript + (interimRef.current ? (transcript ? " " : "") + interimRef.current : "");

  return (
    <div className="space-y-4" data-testid="voice-recorder">
      <div className="rounded-2xl border border-border bg-card p-6 flex flex-col items-center gap-3">
        <div className={cn(
          "h-24 w-24 rounded-full grid place-items-center transition-transform",
          state === "recording" ? "bg-destructive text-white animate-pulse scale-110" : "bg-primary text-primary-foreground",
        )}>
          <Mic className="h-10 w-10" strokeWidth={2.2} />
        </div>
        <div className="text-3xl font-display font-semibold tabular-nums tracking-tight" data-testid="voice-recorder-timer">
          {fmt(duration)}
        </div>
        <div className="text-xs text-muted-foreground text-center">
          {state === "idle" && "Tap the mic to start recording"}
          {state === "recording" && "Recording… speak clearly"}
          {state === "stopped" && `Recording ready — ${fmt(duration)}`}
        </div>

        {micError && (
          <div className="flex items-center gap-2 text-xs text-destructive">
            <AlertCircle className="h-4 w-4" /> {micError}
          </div>
        )}

        {!supportsSR && (
          <Badge variant="secondary" className="rounded-full text-[10px]">
            Live transcript not supported in this browser — use Whisper to improve later
          </Badge>
        )}

        <div className="flex items-center gap-2 pt-2 flex-wrap justify-center">
          {state === "idle" && (
            <Button onClick={start} className="gap-2" data-testid="voice-recorder-start">
              <Mic className="h-4 w-4" /> Start recording
            </Button>
          )}
          {state === "recording" && (
            <Button onClick={stop} variant="destructive" className="gap-2" data-testid="voice-recorder-stop">
              <Square className="h-4 w-4" /> Stop
            </Button>
          )}
          {state === "stopped" && (
            <>
              <Button variant="outline" onClick={togglePlayback} className="gap-2" data-testid="voice-recorder-play">
                {playing ? <><Pause className="h-4 w-4" /> Pause</> : <><Play className="h-4 w-4" /> Play</>}
              </Button>
              <Button variant="outline" onClick={resetAll} className="gap-2" data-testid="voice-recorder-reset">
                <RotateCcw className="h-4 w-4" /> Redo
              </Button>
            </>
          )}
        </div>

        <audio ref={audioEl} onEnded={() => setPlaying(false)} className="hidden" />
      </div>

      {state === "stopped" && (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="voice-title" className="text-xs">Title</Label>
            <Input
              id="voice-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Leave blank to auto-generate from transcript"
              data-testid="voice-recorder-title"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="voice-transcript" className="text-xs">Transcript</Label>
            <Textarea
              id="voice-transcript"
              rows={5}
              value={displayTranscript}
              onChange={(e) => { setTranscript(e.target.value); interimRef.current = ""; }}
              placeholder={supportsSR ? "Transcript appears here as you speak (edit before saving)." : "No live transcript. Save first, then use Whisper Improve to generate one."}
              data-testid="voice-recorder-transcript"
            />
          </div>
        </>
      )}

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          onClick={() => { resetAll(); onCancel?.(); }}
          disabled={saving}
          className="flex-1 gap-1.5"
          data-testid="voice-recorder-cancel"
        >
          <Trash2 className="h-4 w-4" /> Discard
        </Button>
        <Button
          onClick={save}
          disabled={saving || state !== "stopped"}
          className="flex-1 gap-1.5"
          data-testid="voice-recorder-save"
        >
          <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save voice note"}
        </Button>
      </div>
    </div>
  );
}
